import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDocFromServer, setDoc, Timestamp, waitForPendingWrites } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/core/AuthContext';
import { db, functions } from '@/core/firebase';
import type { GoalType, EquipmentType } from '../../../types';
import { NotificationPermissionScreen } from '../../../components/NotificationPermissionScreen';
import { Button, Card } from '../../../components/ui';
import { GOALS, EXPERIENCE_LEVELS, EQUIPMENT_OPTIONS, DURATIONS } from '../api/fitnessConstants';
import type { ExperienceLevel } from '../api/fitnessConstants';

interface OnboardingData {
  displayName: string;
  fitnessGoal: GoalType | null;
  experienceLevel: ExperienceLevel | null;
  equipment: EquipmentType[];
  preferredDuration: 30 | 45 | 60 | null;
  limitations: string;
}

export function OnboardingWizard() {
  const { user, userData, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<OnboardingData>({
    displayName: userData?.displayName || '',
    fitnessGoal: null,
    experienceLevel: null,
    equipment: [],
    preferredDuration: null,
    limitations: '',
  });

  const totalSteps = 7;

  /** Pick a mobility template ID based on onboarding selections */
  const pickMobilityTemplateId = (goal: GoalType | null, experience: string | null): string => {
    if (goal === 'mobility') return 'mobility_full_flow';
    if (experience === 'beginner') return 'mobility_morning_wakeup';
    return 'mobility_morning_wakeup';
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      const onboardingPayload = {
        displayName: data.displayName.trim(),
        fitnessGoal: data.fitnessGoal,
        experienceLevel: data.experienceLevel,
        equipment: data.equipment,
        preferredDuration: data.preferredDuration,
        limitations: data.limitations || '',
        onboardingComplete: true,
        aiTrainerId: 'default-coach',
        updatedAt: Timestamp.now(),
      };

      await setDoc(
        userRef,
        onboardingPayload,
        { merge: true }
      );

      await waitForPendingWrites(db);

      const savedUserDoc = await getDocFromServer(userRef);
      const savedUserData = savedUserDoc.data();

      if (!savedUserData?.onboardingComplete) {
        throw new Error('Onboarding profile did not persist to Firestore.');
      }

      // Auto-assign a daily mobility template
      try {
        const templateId = pickMobilityTemplateId(data.fitnessGoal, data.experienceLevel);
        const assignDefaultDailyTemplate = httpsCallable<
          { templateId: string },
          { success: boolean; templateId: string; clonedFrom: string }
        >(functions, 'assignDefaultDailyTemplate');
        await assignDefaultDailyTemplate({ templateId });
      } catch (e) {
        // Non-critical: don't block onboarding if template assignment fails
        console.warn('Failed to auto-assign mobility template:', e);
      }

      await refreshUserData();
      // Small delay to let React commit the userData state before navigation
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate('/book-intro', { state: { fromOnboarding: true } });
    } catch (error) {
      console.error('Error completing onboarding:', error);
      alert('Failed to save your preferences. Please try again.');
      setSaving(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return data.displayName.trim().length > 0;
      case 2:
        return data.fitnessGoal !== null;
      case 3:
        return data.experienceLevel !== null;
      case 4:
        return data.equipment.length > 0;
      case 5:
        return data.preferredDuration !== null;
      case 6:
        return true;
      case 7:
        return true; // limitations is optional
      default:
        return false;
    }
  };

  const toggleEquipment = (value: EquipmentType) => {
    setData((prev) => {
      const current = prev.equipment;
      if (current.includes(value)) {
        return { ...prev, equipment: current.filter((e) => e !== value) };
      } else {
        return { ...prev, equipment: [...current, value] };
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4" data-testid="onboarding-page">
      <Card className="w-full max-w-2xl" padding="lg" variant="elevated" data-testid="onboarding-wizard">
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600 dark:text-slate-400">
              Step {step} of {totalSteps}
            </span>
            <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
              {Math.round((step / totalSteps) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-300"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Step Content */}
        <div className="mb-8">
          {/* Step 1: Welcome */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  Welcome to Start Grounded!
                </h2>
                <p className="text-gray-600 dark:text-slate-400">
                  Let's set up your fitness profile
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  What should we call you?
                </label>
                <input
                  data-testid="onboarding-name-input"
                  type="text"
                  value={data.displayName}
                  onChange={(e) => setData({ ...data, displayName: e.target.value })}
                  placeholder="Your first name"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition"
                  autoFocus
                />
              </div>
            </div>
          )}

          {/* Step 2: Goals */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  What's your primary goal?
                </h2>
                <p className="text-gray-600 dark:text-slate-400">
                  This helps us personalize your workouts
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {GOALS.map((goal) => (
                  (() => {
                    const Icon = goal.icon;
                    return (
                      <button
                        key={goal.value}
                        data-testid={`onboarding-goal-${goal.value}`}
                        onClick={() => setData({ ...data, fitnessGoal: goal.value })}
                        className={`
                          p-4 rounded-xl border-2 transition-all text-left
                          ${
                            data.fitnessGoal === goal.value
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-gray-200 dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-700'
                          }
                        `}
                      >
                        <div className="mb-2 text-gray-900 dark:text-white">
                          <Icon size={28} weight="bold" />
                        </div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {goal.label}
                        </div>
                      </button>
                    );
                  })()
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Experience */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  What's your experience level?
                </h2>
                <p className="text-gray-600 dark:text-slate-400">
                  We'll adjust difficulty accordingly
                </p>
              </div>
              <div className="space-y-3">
                {EXPERIENCE_LEVELS.map((level) => (
                  <button
                    key={level.value}
                    data-testid={`onboarding-experience-${level.value}`}
                    onClick={() => setData({ ...data, experienceLevel: level.value })}
                    className={`
                      w-full p-4 rounded-xl border-2 transition-all text-left
                      ${
                        data.experienceLevel === level.value
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-700'
                      }
                    `}
                  >
                    <div className="font-medium text-gray-900 dark:text-white mb-1">
                      {level.label}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-slate-400">
                      {level.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Equipment */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  What equipment do you have?
                </h2>
                <p className="text-gray-600 dark:text-slate-400">
                  Select all that apply
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {EQUIPMENT_OPTIONS.map((equipment) => (
                  (() => {
                    const Icon = equipment.icon;
                    return (
                      <button
                        key={equipment.value}
                        data-testid={`onboarding-equipment-${equipment.value}`}
                        onClick={() => toggleEquipment(equipment.value)}
                        className={`
                          p-4 rounded-xl border-2 transition-all text-left
                          ${
                            data.equipment.includes(equipment.value)
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-gray-200 dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-700'
                          }
                        `}
                      >
                        <div className="mb-2 text-gray-900 dark:text-white">
                          <Icon size={28} weight="bold" />
                        </div>
                        <div className="font-medium text-gray-900 dark:text-white text-sm">
                          {equipment.label}
                        </div>
                      </button>
                    );
                  })()
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Duration */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  How long are your typical workouts?
                </h2>
                <p className="text-gray-600 dark:text-slate-400">
                  Choose your preferred session length
                </p>
              </div>
              <div className="space-y-3">
                {DURATIONS.map((duration) => (
                  <button
                    key={duration.value}
                    data-testid={`onboarding-duration-${duration.value}`}
                    onClick={() => setData({ ...data, preferredDuration: duration.value })}
                    className={`
                      w-full p-5 rounded-xl border-2 transition-all text-center
                      ${
                        data.preferredDuration === duration.value
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-700'
                      }
                    `}
                  >
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {duration.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 6: Notifications */}
          {step === 6 && (
            <NotificationPermissionScreen onComplete={handleNext} onSkip={handleNext} />
          )}

          {/* Step 7: Limitations */}
          {step === 7 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Any injuries or limitations?
                </h2>
                <p className="text-gray-600 dark:text-slate-400">
                  Optional - this helps us keep you safe
                </p>
              </div>
              <div>
                <textarea
                  data-testid="onboarding-limitations-input"
                  value={data.limitations}
                  onChange={(e) => setData({ ...data, limitations: e.target.value })}
                  placeholder="E.g., bad knee, shoulder injury, lower back issues..."
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition resize-none"
                />
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/30 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg
                    className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="text-sm text-blue-900 dark:text-blue-300">
                    <p className="font-medium mb-1">Almost done!</p>
                    <p>
                      We'll use this info to create your personalized workout program. You can
                      always update your preferences later in settings.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex gap-3">
          {step > 1 && (
            <Button
              variant="ghost"
              data-testid="onboarding-back-button"
              onClick={handleBack}
              disabled={saving}
              className="flex-1"
            >
              Back
            </Button>
          )}
          {step < totalSteps && step !== 6 ? (
            <Button
              variant="primary"
              data-testid="onboarding-continue-button"
              onClick={handleNext}
              disabled={!canProceed()}
              className="flex-1"
            >
              Continue
            </Button>
          ) : step === totalSteps ? (
            <Button
              variant="primary"
              data-testid="onboarding-complete-button"
              onClick={handleComplete}
              disabled={!canProceed() || saving}
              isLoading={saving}
              className="flex-1"
            >
              {saving ? 'Saving...' : 'Complete Setup'}
            </Button>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
